import React, { useEffect, useMemo, useRef, useState } from "react";
import { message } from "antd";
import EnhancedTable from "@/hocComponents/EnhancedTable";
import http from "@/utils/http";
import api from "@/utils/api";


const titleWithCount = (title, count) => (
  <div style={{ lineHeight: 1.3 }}>
    <div>{title}</div>
   <div style={{ fontWeight: 400, color: "#595959" }}>({count ?? 0})</div>
  </div>
);

const mergeCell = (record) => ({
  rowSpan: record.rowSpan,
  style: { verticalAlign: "middle", textAlign: "center" },
});

export default function OutstandingTab({ year, refreshKey, onLoadingChange }) {
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState([]);
  const requestIdRef = useRef(0);

  const load = async () => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    try {
      const res = await http.post(api.getOutstandingTransactions, { year });
      if (requestId !== requestIdRef.current) return;
      if (res?.code === "1") {
        setList(res.data?.list || []);
      } else {
        message.error(res?.msg);
      }
    } catch (e) {
      if (requestId !== requestIdRef.current) return;
      message.error("Failed to load data, please try again later");
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [year, refreshKey]);

  useEffect(() => {
    onLoadingChange?.(loading);
  }, [loading]);

  const header = list[0];
  const headerEntityCount = header?.entityCount;
  const headerUnrecTransCount = header?.totalBankUnrecTransCount;

  const columns = useMemo(
    () => [
      {
        title: titleWithCount("Entity", headerEntityCount),
        dataIndex: "entity",
        onCell: mergeCell,
        width: 200,
        render: (_, record) => <div>{record.entity}</div>,
      },
      {
        title: titleWithCount("Total Bank Unrec Trans", headerUnrecTransCount),
        dataIndex: "totalBankUnrecTrans",
        onCell: mergeCell,
        render: (_, record) => <div>{record.totalBankUnrecTrans}</div>,
      },
      { title: "Month - Year", dataIndex: "monthYear", align: "left", width: 80 },
      { title: "Credit", dataIndex: "credit", align: "left", width: 80 },
      { title: "Debit", dataIndex: "debit", align: "left", width: 80 },
      { title: "Cash Receive", dataIndex: "cashReceive", align: "left", width: 80 },
    ],
    [headerEntityCount, headerUnrecTransCount],
  );

  const rows = useMemo(
    () =>
      list.flatMap((item) => {
        const months = item.monthList?.length ? item.monthList : [null];
        return months.map((m, idx) => ({
          rowKey: `${item.trustId}-${m?.monthYear ?? "none"}-${idx}`,
          trustId: item.trustId,
          entity: item.entity,
          totalBankUnrecTrans: item.totalBankUnrecTrans,
          monthYear: m?.monthYear ?? "",
          credit: m?.credit ?? 0,
          debit: m?.debit ?? 0,
          cashReceive: m?.cashReceive ?? 0,
          rowSpan: idx === 0 ? months.length : 0,
        }));
      }),
    [list],
  );

  return (
    <div style={{ width: "50%", minWidth: 640, maxWidth: "100%" }}>
      <EnhancedTable
        columns={columns}
        dataSource={rows}
        loading={loading}
        rowKey="rowKey"
        pagination={false}
        bordered
        size="small"
        scroll={{ y: "calc(100vh - 250px)" }}
      />
    </div>
  );
}